import type { EffectRegion } from "@client/types/vision";
import type { FC } from "react";

export interface RegionControlsProps {
	effectRegion: EffectRegion | null;
	regionDrawMode: "none" | "drawing";
	regionPoints: Array<{ x: number; y: number }>;
	onStartDrawing: () => void;
	onUndoPoint: () => void;
	onCancelDrawing: () => void;
	onClearRegion: () => void;
}

const BTN_BASE =
	"flex-1 px-3 py-3 text-sm rounded font-semibold transition-colors min-h-[48px]";

export const RegionControls: FC<RegionControlsProps> = ({
	effectRegion,
	regionDrawMode,
	regionPoints,
	onStartDrawing,
	onUndoPoint,
	onCancelDrawing,
	onClearRegion,
}) => {
	const drawing = regionDrawMode === "drawing";
	const hasRegion = effectRegion !== null;

	const wholeImageActive = !hasRegion && !drawing;
	const drawRegionActive = hasRegion || drawing;

	return (
		<>
			<div className="flex gap-2 mb-3">
				<button
					type="button"
					className={`${BTN_BASE} ${
						wholeImageActive
							? "bg-blue-500 text-white"
							: "bg-gray-200 text-gray-700"
					}`}
					onClick={onClearRegion}
				>
					Whole Image
				</button>
				<button
					type="button"
					className={`${BTN_BASE} ${
						drawing
							? "bg-orange-500 text-white"
							: drawRegionActive
								? "bg-blue-500 text-white"
								: "bg-gray-200 text-gray-700"
					}`}
					onClick={drawing ? undefined : onStartDrawing}
				>
					{drawing
						? `Drawing (${regionPoints.length} ${regionPoints.length === 1 ? "point" : "points"})`
						: "Draw Region"}
				</button>
			</div>

			{drawing && (
				<div className="mb-3 rounded bg-orange-50 border border-orange-200 p-3">
					<div className="text-xs text-orange-900 font-medium mb-2 leading-relaxed">
						Tap the image to add points. Tap near your first point or double-tap
						to close. Minimum 3 points.
					</div>
					<div className="flex gap-2">
						<button
							type="button"
							className="flex-1 min-h-[44px] text-sm font-semibold bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-40"
							onClick={onUndoPoint}
							disabled={regionPoints.length === 0}
						>
							Undo last
						</button>
						<button
							type="button"
							className="flex-1 min-h-[44px] text-sm font-semibold bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
							onClick={onCancelDrawing}
						>
							Cancel
						</button>
					</div>
				</div>
			)}

			{hasRegion && !drawing && (
				<div className="flex items-center gap-2 mb-3">
					<span className="text-xs text-blue-600 font-medium">
						{effectRegion?.type === "polygon"
							? `Region active (${effectRegion.points.length} vertices)`
							: "Region active"}
					</span>
					<button
						type="button"
						className="text-xs text-red-500 underline"
						onClick={onClearRegion}
					>
						Clear
					</button>
				</div>
			)}
		</>
	);
};
